<script setup>
/* eslint-env vue/setup-compiler-macros */
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import IftaLabel from 'primevue/iftalabel'
import Button from 'primevue/button'
import Inplace from 'primevue/inplace'
import { useI18n } from '@/i18n'

const props = defineProps({
  streams: { type: Array, default: () => [] },
  streamsLoading: { type: Boolean, default: false },
  streamsError: { type: String, default: '' },
  canEdit: { type: Boolean, default: false },
  changesSaving: { type: Boolean, default: false },
  hasUnsavedChanges: { type: Boolean, default: false },
  streamTitle: { type: Function, required: true },
  conditionList: { type: Function, required: true },
  conditionFieldValue: { type: Function, required: true },
  conditionTypeOptions: { type: Array, default: () => [] },
  conditionOperatorOptions: { type: Array, default: () => [] },
  condRowClass: { type: Function, required: true },
  updateConditionField: { type: Function, required: true },
  triggerConfig: { type: Function, required: true },
  operatorOptions: { type: Array, default: () => [] },
  updateTriggerOperator: { type: Function, required: true },
  updateTriggerCount: { type: Function, required: true },
  hasNextStream: { type: Function, required: true },
  hasNextWindow: { type: Function, required: true },
  nextStreamLabel: { type: Function, required: true },
  nextWindowDraft: { type: Function, required: true },
  nextWindowUnitLabel: { type: Function, required: true },
  nextWindowUnits: { type: Array, default: () => [] },
  updateNextWindowValue: { type: Function, required: true },
  updateNextWindowUnit: { type: Function, required: true },
  openStreamDialog: { type: Function, required: true },
  saveStreamChanges: { type: Function, required: true },
  openConditionModal: { type: Function, required: true },
  moveStream: { type: Function, required: true },
  removeStream: { type: Function, required: true },
  deleteCondition: { type: Function }
})

const { t } = useI18n()
</script>

<template>
  <pFieldset class="legend-transparent dashboard-widget">
    <template #legend>
      <div class="flex items-center pl-2">
        <span class="font-bold p-2">{{ t('siemWorkflowDetails.streamsTitle') }}</span>
      </div>
    </template>
    <div class="flex align-items-center justify-content-between gap-2 mb-2">
      <div>
        <p class="muted m-0">{{ t('siemWorkflowDetails.streamsLinked', { count: props.streams.length || 0 }) }}</p>
      </div>
      <div class="flex gap-2 align-items-center flex-wrap">
        <Button
          v-if="props.canEdit"
          icon="pi pi-save"
          :label="t('common.save')"
          size="small"
          severity="success"
          :disabled="!props.hasUnsavedChanges || props.changesSaving"
          :loading="props.changesSaving"
          @click="props.saveStreamChanges"
        />
        <Button
          v-if="props.canEdit"
          icon="pi pi-plus"
          :label="t('siemWorkflowDetails.streamAdd')"
          @click="props.openStreamDialog"
          severity="secondary"
          size="small"
        />
      </div>
    </div>

    <div v-if="props.streamsLoading" class="muted">{{ t('siemWorkflowDetails.props.streamsLoading') }}</div>
    <div v-else-if="props.streamsError" class="muted error-text">{{ props.streamsError }}</div>
    <div v-else-if="!props.streams.length" class="empty-state">
      <p class="muted">{{ t('siemWorkflowDetails.streamsEmpty') }}</p>
    </div>

    <div v-else class="mt-3">
      <div
        v-for="(stream, index) in props.streams"
        :key="stream.id"
        class="stream-item"
      >
        <div class="stream-header">
            <div class="stream-title">
              <div class="stream-pill">#{{ stream.order_index }}</div>
              <h4>{{ props.streamTitle(stream) }}</h4>
            </div>
            <div class="stream-actions" v-if="props.canEdit">
              <Button
                icon="pi pi-arrow-up"
                size="small"
                text
                :disabled="index === 0"
                @click="props.moveStream(index, -1)"
              />
              <Button
                icon="pi pi-arrow-down"
                size="small"
                text
                :disabled="index === props.streams.length - 1"
                @click="props.moveStream(index, 1)"
              />
              <Button
                icon="pi pi-trash"
                @click="props.removeStream(stream.id)"
                severity="danger"
                size="small"
                text
              />
          </div>
        </div>
        <div class="stream-meta">
          <span v-if="stream.guid" class="stream-guid">{{ t('siemWorkflowDetails.guidLabel') }}: {{ stream.guid }}</span>
          <span v-if="stream.category">· {{ stream.category }}</span>
          <span v-if="stream.subcategory">· {{ stream.subcategory }}</span>
        </div>
        <div class="flex flex-column gap-2 pt-2">
          <div class="flex justify-content-end" v-if="props.canEdit">
            <Button
              icon="pi pi-plus"
              :label="t('siemWorkflowDetails.conditionAdd')"
              size="small"
              text
              @click="props.openConditionModal(stream)"
            />
          </div>
          <ul v-if="props.conditionList(stream).length" class="condition-list">
            <li
              v-for="(cond, cIndex) in props.conditionList(stream)"
              :key="`cond-${stream.id}-${cond.id || cIndex}`"
              class="condition-item"
            >
              <i class="pi pi-filter-fill"></i>
                <div class="condition-item-content">
                <div class="condition-row-wrap">
                <div
                    class="condition-row"
                    :class="props.condRowClass(props.conditionFieldValue(cond, 'type'))"
                >
                    <template v-if="props.conditionFieldValue(cond, 'type') === 'Zeitfenster'">
                      <div>
                        <IftaLabel>
                          <Select
                            :input-id="`cond-${stream.id}-${cIndex}-type`"
                            :options="props.conditionTypeOptions"
                            optionLabel="label"
                            optionValue="value"
                            :model-value="props.conditionFieldValue(cond, 'type')"
                            size="small"
                            class="w-full condition-select"
                            :disabled="!props.canEdit || !cond.id"
                            @update:model-value="(value) => props.updateConditionField(stream, cond, 'type', value)"
                          />
                          <label :for="`cond-${stream.id}-${cIndex}-type`">{{ t('siemWorkflowDetails.conditionType') }}</label>
                        </IftaLabel>
                      </div>
                      <div class="condition-span-3">
                        <IftaLabel>
                          <InputText
                            :id="`cond-${stream.id}-${cIndex}-cron`"
                            :model-value="props.conditionFieldValue(cond, 'cron')"
                            size="small"
                            class="w-full condition-input"
                            :disabled="!props.canEdit || !cond.id"
                            @update:model-value="(value) => props.updateConditionField(stream, cond, 'cron', value)"
                          />
                          <label :for="`cond-${stream.id}-${cIndex}-cron`">{{ t('siemWorkflowDetails.conditionCron') }}</label>
                        </IftaLabel>
                      </div>
                    </template>
                    <template v-else>
                      <div>
                        <IftaLabel>
                          <Select
                            :input-id="`cond-${stream.id}-${cIndex}-type`"
                          :options="props.conditionTypeOptions"
                            optionLabel="label"
                            optionValue="value"
                            :model-value="props.conditionFieldValue(cond, 'type')"
                            size="small"
                            class="w-full condition-select"
                            :disabled="!props.canEdit || !cond.id"
                            @update:model-value="(value) => props.updateConditionField(stream, cond, 'type', value)"
                          />
                          <label :for="`cond-${stream.id}-${cIndex}-type`">{{ t('siemWorkflowDetails.conditionType') }}</label>
                        </IftaLabel>
                      </div>
                      <div class="condition-span-2">
                        <IftaLabel>
                        <InputText
                            :id="`cond-${stream.id}-${cIndex}-field`"
                            :model-value="props.conditionFieldValue(cond, 'field')"
                            size="small"
                            class="w-full condition-input"
                            :disabled="!props.canEdit || !cond.id"
                            @update:model-value="(value) => props.updateConditionField(stream, cond, 'field', value)"
                          />
                          <label :for="`cond-${stream.id}-${cIndex}-field`">{{ t('siemWorkflowDetails.conditionField') }}</label>
                        </IftaLabel>
                      </div>
                      <div v-if="props.conditionFieldValue(cond, 'type') === 'Filter'">
                        <IftaLabel>
                          <Select
                            :input-id="`cond-${stream.id}-${cIndex}-operator`"
                            :options="props.conditionOperatorOptions"
                            optionLabel="label"
                            optionValue="value"
                            :model-value="props.conditionFieldValue(cond, 'operator')"
                            size="small"
                            class="w-full condition-select"
                          :disabled="!props.canEdit || !cond.id"
                          @update:model-value="(value) => props.updateConditionField(stream, cond, 'operator', value)"
                          />
                          <label :for="`cond-${stream.id}-${cIndex}-operator`">{{ t('siemWorkflowDetails.conditionOperator') }}</label>
                        </IftaLabel>
                      </div>
                      <div v-if="props.conditionFieldValue(cond, 'type') === 'Filter'">
                        <IftaLabel>
                          <InputText
                            :id="`cond-${stream.id}-${cIndex}-value`"
                            :model-value="props.conditionFieldValue(cond, 'value')"
                            size="small"
                            class="w-full condition-input"
                            :disabled="!props.canEdit || !cond.id"
                            @update:model-value="(value) => props.updateConditionField(stream, cond, 'value', value)"
                          />
                          <label :for="`cond-${stream.id}-${cIndex}-value`">{{ t('siemWorkflowDetails.conditionValue') }}</label>
                        </IftaLabel>
                      </div>
                    </template>
                  </div>
                  <Button
                    v-if="props.canEdit && cond.id"
                    icon="pi pi-trash"
                    size="small"
                    text
                    severity="danger"
                    class="condition-delete"
                    @click="props.deleteCondition?.(stream, cond)"
                  />
                </div>
              </div>
            </li>
          </ul>
          <div v-else class="empty-state">
            <p class="muted">{{ t('siemWorkflowDetails.conditionEmpty') }}</p>
          </div>
        </div>

        <div class="flex flex-column gap-2 pt-2">
          <ul class="inline-list">
            <li class="inline-item">
              <i class="pi pi-check-circle"></i>
              {{ t('siemWorkflowDetails.triggerPrefix') }}
              <Inplace class="inline-edit" :disabled="!props.canEdit">
                <template #display>
                  <span class="inline-value">{{ props.triggerConfig(stream).operatorLabel }}</span>
                </template>
                <template #content="{ closeCallback }">
                  <span class="inline-edit-row">
                    <Select
                      class="inline-dropdown"
                      :options="props.operatorOptions"
                      optionLabel="label"
                      optionValue="value"
                      :model-value="props.triggerConfig(stream).operatorValue"
                      @update:model-value="(value) => props.updateTriggerOperator(stream, value)"
                    />
                    <Button icon="pi pi-times" text severity="danger" @click="closeCallback" />
                  </span>
                </template>
              </Inplace>
              {{ t('siemWorkflowDetails.triggerMiddle') }}
              <Inplace class="inline-edit" :disabled="!props.canEdit">
                <template #display>
                  <span class="inline-value">{{ props.triggerConfig(stream).count }}</span>
                </template>
                <template #content="{ closeCallback }">
                  <span class="inline-edit-row">
                    <InputText
                      type="number"
                      class="inline-number"
                      :model-value="props.triggerConfig(stream).count"
                      @update:model-value="(value) => props.updateTriggerCount(stream, value)"
                      min="1"
                    />
                    <Button icon="pi pi-times" text severity="danger" @click="closeCallback" />
                  </span>
                </template>
              </Inplace>
              {{ t('siemWorkflowDetails.triggerSuffix') }}
            </li>
          </ul>
        </div>
        <div v-if="props.hasNextStream(index) && props.hasNextWindow(stream)" class="flex flex-column gap-2 pt-2">
          <ul class="inline-list">
            <li class="inline-item">
              <i class="pi pi-check-circle"></i>
              {{ t('siemWorkflowDetails.nextStreamPrefix') }}
              <span class="next-name">"{{ props.nextStreamLabel(index) }}"</span>
              {{ t('siemWorkflowDetails.nextStreamWithin') }}
              <Inplace class="inline-edit" :disabled="!props.canEdit">
                <template #display>
                  <span class="inline-value">{{ props.nextWindowDraft(stream).value }}</span>
                </template>
                <template #content="{ closeCallback }">
                  <span class="inline-edit-row">
                    <InputText
                      type="number"
                      class="inline-number"
                      :model-value="props.nextWindowDraft(stream).value"
                      @update:model-value="(value) => props.updateNextWindowValue(stream, value)"
                      min="1"
                    />
                    <Button icon="pi pi-times" text severity="danger" @click="closeCallback" />
                  </span>
                </template>
              </Inplace>
              <Inplace class="inline-edit" :disabled="!props.canEdit">
                <template #display>
                  <span class="inline-value">{{ props.nextWindowUnitLabel(props.nextWindowDraft(stream).unit) }}</span>
                </template>
                <template #content="{ closeCallback }">
                  <span class="inline-edit-row">
                    <Select
                      class="inline-dropdown"
                      :options="props.nextWindowUnits"
                      optionLabel="label"
                      optionValue="value"
                      :model-value="props.nextWindowDraft(stream).unit"
                      @update:model-value="(value) => props.updateNextWindowUnit(stream, value)"
                    />
                    <Button icon="pi pi-times" text severity="danger" @click="closeCallback" />
                  </span>
                </template>
              </Inplace>
              {{ t('siemWorkflowDetails.nextStreamSuffix') }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </pFieldset>
</template>

<style scoped>
.muted {
  color: var(--p-text-color-secondary);
  opacity: 0.8;
}

.error-text {
  color: var(--p-red-500, #ef4444);
}

.empty-state {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px dashed var(--p-content-border-color, rgba(0,0,0,.08));
  border-radius: 10px;
  background: var(--p-surface-50, rgba(0,0,0,.012));
}

.stream-item {
  border: 1px solid var(--p-content-border-color, rgba(0,0,0,.08));
  border-radius: 10px;
  padding: 12px;
  background: var(--p-surface-50, rgba(0,0,0,.012));
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stream-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.stream-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.stream-title h4 {
  margin: 0;
  font-size: 1.05em;
}

.stream-meta {
  display: flex;
  gap: 6px;
  align-items: baseline;
  font-size: 0.9em;
  color: var(--p-text-color-secondary);
}

.stream-pill {
  padding: 4px 8px;
  border-radius: 12px;
  background: var(--p-primary-100);
  color: var(--p-primary-700);
  font-weight: 700;
}

.stream-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.condition-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.condition-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.condition-item .pi {
  margin-top: 6px;
}

.condition-item-content {
  flex: 1;
  min-width: 0;
}

.condition-row-wrap {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.condition-row {
  flex: 1;
  min-width: 0;
  display: grid;
  /* Schmale Sidebar: zwei gleichmäßige Spalten, Feld/Wert etwas breiter */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 10px 8px;
}

/* Group / Display Field: Typ | Feld */
.condition-row--short {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
}

/* Zeitfenster: Typ und Cron-Ausdruck gestapelt (Cron ist lang) */
.condition-row--time {
  grid-template-columns: minmax(0, 1fr);
}

/* Spans aufheben — jede Zelle füllt genau eine Grid-Spalte für saubere Ausrichtung */
.condition-span-2,
.condition-span-3 {
  grid-column: auto;
}

.condition-select,
.condition-input {
  font-size: 0.9em;
}

/* IftaLabel: bei size="small" überlappt sonst das Label den Wert —
   genug Platz oben für das Label schaffen. */
.condition-row :deep(.p-iftalabel) {
  display: block;
  width: 100%;
}
.condition-row :deep(.p-iftalabel > label) {
  top: 0.45rem;
  margin-top: 0;
  font-size: 0.68rem;
  line-height: 1;
}
.condition-row :deep(.p-iftalabel .p-inputtext),
.condition-row :deep(.p-iftalabel .p-select-label) {
  padding-top: 1.15rem;
  padding-bottom: 0.3rem;
}

.condition-delete {
  margin-top: 0.85rem;
  flex: 0 0 auto;
}

@media (max-width: 360px) {
  .condition-row,
  .condition-row--short {
    grid-template-columns: minmax(0, 1fr);
  }
}

.inline-edit {
  display: inline-flex;
}

.inline-list {
  margin: 0;
  padding-left: 18px;
  color: var(--p-text-color);
  font-size: 0.9em;
}

.inline-item {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px;
}

.inline-item .pi {
  margin-right: 4px;
}

.next-name {
  font-weight: 600;
}

.inline-edit :deep(.p-inplace-display) {
  display: inline-flex;
  align-items: center;
  border-bottom: 1px dashed var(--p-content-border-color, rgba(0,0,0,.08));
  padding-bottom: 2px;
  cursor: pointer;
}

.inline-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 0.95em;
  color: var(--p-text-color);
}

.inline-edit-row {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.inline-number {
  width: 90px;
}

.inline-dropdown {
  min-width: 140px;
}
</style>
